if (!customElements.get('subscription-picker')) {
  customElements.define(
    'subscription-picker',
    class SubscriptionPicker extends HTMLElement {
      constructor() {
        super()
        this.product = null
        this.selectedVariant = null
        this.selectedSellingPlan = null
        this.availableSellingPlanGroups = []
        this.checkbox = null
        this.frequencyContainer = null
        this.frequencyButtons = []
        this.sellingPlanInput = null
        this.productForm = null
        // Monotonic counter used by updatePrice() to ignore stale fetch
        // responses when the shopper rapidly changes variant/plan.
        this._priceRequestToken = 0
      }

      connectedCallback() {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.init())
        } else {
          this.init()
        }
      }

      init() {
        this.loadProductData()
        this.findProductForm()
        this.setupVariantListener()
      }

      loadProductData() {
        const productJson = this.querySelector('[data-product-json]')
        if (!productJson) {
          console.warn('SubscriptionPicker: No product JSON found')
          return
        }

        try {
          this.product = JSON.parse(productJson.textContent)
          if (!this.product) {
            console.warn('SubscriptionPicker: Product data is null')
            return
          }

          // Gotcha: `{{ product | json }}` does NOT serialize
          // `selected_or_first_available_variant` (it's a Liquid accessor,
          // not a JSON field), so the shopper-selected variant has to be
          // recovered from the URL or the product form. If we blindly fell
          // back to `this.product.variants[0]` here, subscribing to a page
          // loaded on a non-default variant (e.g. a bundle variant worth
          // $32) would fetch subscription prices for the $12 default
          // variant instead.
          const initialVariantId = this.getInitialSelectedVariantId()
          if (initialVariantId) {
            this.selectedVariant = this.product.variants.find((v) => v.id === initialVariantId)
          }
          if (!this.selectedVariant) {
            this.selectedVariant = this.product.variants?.[0]
          }
          if (!this.selectedVariant) {
            console.warn('SubscriptionPicker: No variant found')
            return
          }

          this.updateAvailableSellingPlanGroups()
          this.render()
        } catch (error) {
          console.error('SubscriptionPicker: Failed to parse product data', error)
        }
      }

      /**
       * Resolve which variant was active when the page was rendered. Prefers
       * the URL's `?variant=` param (Shopify's canonical signal) and falls
       * back to whatever the product form's hidden `[name="id"]` input was
       * set to by Liquid (which comes from `selected_or_first_available_variant`
       * and therefore already accounts for that URL param as well as any
       * server-side defaulting for unavailable variants).
       *
       * @returns {number|null}
       */
      getInitialSelectedVariantId() {
        try {
          const params = new URLSearchParams(window.location.search)
          const urlVariantId = parseInt(params.get('variant'), 10)
          if (urlVariantId) return urlVariantId
        } catch (e) {
          // URLSearchParams / window.location unavailable — ignore.
        }

        const productInfo = this.closest('.product__info-container')
        const sectionId = this.dataset.sectionId || productInfo?.dataset.section
        const formId = this.dataset.productFormId || (sectionId ? `product-form-${sectionId}` : null)
        if (formId) {
          const form = document.getElementById(formId)
          const input = form?.querySelector('input[name="id"]')
          const formVariantId = parseInt(input?.value, 10)
          if (formVariantId) return formVariantId
        }

        return null
      }

      updateAvailableSellingPlanGroups() {
        if (!this.product || !this.selectedVariant) return

        this.availableSellingPlanGroups = this.product.selling_plan_groups.filter((group) =>
          group.selling_plans.some((plan) =>
            this.selectedVariant.selling_plan_allocations?.some((allocation) => allocation.selling_plan_id === plan.id)
          )
        )
      }

      getAvailableSellingPlans(group) {
        if (!this.selectedVariant) return []
        const availablePlans = group.selling_plans.filter((plan) =>
          this.selectedVariant.selling_plan_allocations?.some((allocation) => allocation.selling_plan_id === plan.id)
        )

        // Dedupe based on name
        const seenNames = new Set()
        const dedupedPlans = availablePlans.filter((plan) => {
          const name = plan.name.toLowerCase()
          if (seenNames.has(name)) {
            return false
          }
          seenNames.add(name)
          return true
        })

        // Normalize plan names - add "every" if not present
        return dedupedPlans.map((plan) => {
          const normalizedPlan = { ...plan }
          const nameLower = plan.name.toLowerCase()
          if (!nameLower.includes('every')) {
            normalizedPlan.name = `Every ${plan.name}`
          }
          return normalizedPlan
        })
      }

      setupVariantListener() {
        const productInfo = this.closest('.product__info-container')
        const sectionId = this.dataset.sectionId || productInfo?.dataset.section
        const formId = this.dataset.productFormId || (sectionId ? `product-form-${sectionId}` : null)

        if (formId) {
          this.productForm = document.getElementById(formId)

          if (this.productForm) {
            const variantInput = this.productForm.querySelector('[name="id"]')
            if (variantInput) {
              variantInput.addEventListener('change', () => {
                const variantId = parseInt(variantInput.value)
                this.selectedVariant = this.product.variants.find((v) => v.id === variantId)
                this.updateAvailableSellingPlanGroups()
                this.render()
                // Do not call updatePrice() here. VariantSelects.renderProductInfo()
                // now carries any active selling_plan through its section-render
                // fetch, which means the button/price markup returned by Shopify
                // is already subscription-adjusted. Firing our own fetch in
                // parallel would race with it and sometimes clobber the correct
                // result with the earlier response.
              })
            }
          }
        }
      }

      findProductForm() {
        const productInfo = this.closest('.product__info-container')
        const sectionId = this.dataset.sectionId || productInfo?.dataset.section
        const formId = this.dataset.productFormId || (sectionId ? `product-form-${sectionId}` : null)

        if (formId) {
          const tryFindForm = () => {
            this.productForm = document.getElementById(formId)

            if (this.productForm && this.sellingPlanInput) {
              const existingInput = this.productForm.querySelector('input[name="selling_plan"]')
              if (existingInput && existingInput !== this.sellingPlanInput) {
                existingInput.remove()
              }
              if (this.sellingPlanInput.parentNode !== this.productForm) {
                this.productForm.appendChild(this.sellingPlanInput)
              }
            } else if (!this.productForm) {
              setTimeout(tryFindForm, 100)
            }
          }

          tryFindForm()
        }
      }

      render() {
        if (!this.product) {
          console.warn('SubscriptionPicker: No product data available')
          return
        }

        if (this.availableSellingPlanGroups.length === 0) {
          console.warn('SubscriptionPicker: No available selling plan groups for variant', this.selectedVariant?.id)
          return
        }

        const firstGroup = this.availableSellingPlanGroups[0]
        const availablePlans = this.getAvailableSellingPlans(firstGroup)

        if (availablePlans.length === 0) {
          console.warn('SubscriptionPicker: No available plans in group', firstGroup.name)
          return
        }

        const firstPlan = availablePlans[0]
        const discountPercentage = this.getDiscountPercentage(firstPlan)
        const productJsonScript = this.querySelector('[data-product-json]')
        const productJsonHtml = productJsonScript ? productJsonScript.outerHTML : ''
        const pickerId = `subscription-picker-${this.dataset.sectionId || this.dataset.productId || this.product.id}`
        const checkboxId = `${pickerId}-checkbox`
        const checkboxLabelId = `${pickerId}-label`
        const frequencyGroupId = `${pickerId}-frequencies`

        this.innerHTML = `
          ${productJsonHtml}
          <input type="hidden" name="selling_plan" value="">
          <div class="subscription-picker__checkbox-wrapper flex items-center gap-2">
            <input
              type="checkbox"
              id="${checkboxId}"
              class="subscription-picker__checkbox w-5 h-5 rounded border-2 border-black focus:ring-black cursor-pointer"
              aria-label="Subscribe and save"
              aria-labelledby="${checkboxLabelId}"
              aria-controls="${frequencyGroupId}"
              aria-expanded="false"
            >
            <label
              for="${checkboxId}"
              id="${checkboxLabelId}"
              class="subscription-picker__label text-sm cursor-pointer"
              style="font-family: var(--font-primary);"
            >
              ${discountPercentage > 0 ? `Save ${discountPercentage}% with Subscription` : 'Save with Subscription'}
            </label>
          </div>
          <div
            id="${frequencyGroupId}"
            class="subscription-picker__frequencies subscription-picker__frequencies--hidden flex flex-wrap gap-2"
            role="group"
            aria-label="Subscription frequency"
            aria-hidden="true"
          >
            ${availablePlans
              .map(
                (plan) => `
              <button
                type="button"
                class="subscription-picker__frequency px-2 py-1 text-sm font-semibold border-2 border-black rounded-full hover:border-black transition-colors focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
                data-plan-id="${plan.id}"
                aria-pressed="false"
                aria-label="Select ${this.getFrequencyText(plan)}"
                tabindex="-1"
              >
                ${this.getFrequencyText(plan)}
              </button>
            `
              )
              .join('')}
          </div>
          <style>
            .subscription-picker__checkbox {
              display: inline-block !important;
              visibility: visible !important;
              opacity: 1 !important;
              accent-color: black;
              -webkit-appearance: checkbox;
              appearance: checkbox;
            }
            .subscription-picker__checkbox:checked {
              accent-color: black;
              background-color: black;
            }
            input[type="checkbox"].subscription-picker__checkbox:checked {
              background-color: black;
              border-color: black;
              accent-color: black;
            }
            .subscription-picker__checkbox:focus-visible,
            .subscription-picker__frequency:focus-visible {
              outline: 2px solid black;
              outline-offset: 2px;
            }
            .subscription-picker__frequency {
              background-color: transparent;
              color: black;
            }
            .subscription-picker__frequency--selected {
              background-color: black !important;
              color: white !important;
              border-color: black !important;
            }
            .subscription-picker__frequencies {
              max-height: 0;
              opacity: 0;
              transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out;
              margin-top: 0;
            }
            .subscription-picker__frequencies:not(.subscription-picker__frequencies--hidden) {
              max-height: 100px;
              opacity: 1;
              margin-top: 1rem;
            }
          </style>
        `

        this.setupEventListeners()
        this.findProductForm()
        // Do NOT call updatePrice() on initial render: the server already
        // rendered the correct market-currency price into `.priced`, and the
        // raw `variant.price` in product JSON is always in the shop's base
        // currency cents. Overwriting here would clobber the market price
        // with an un-converted base amount.
      }

      setupEventListeners() {
        this.checkbox = this.querySelector('input[type="checkbox"]')
        this.frequencyContainer = this.querySelector('.subscription-picker__frequencies')
        this.frequencyButtons = Array.from(this.querySelectorAll('.subscription-picker__frequency'))
        this.sellingPlanInput = this.querySelector('input[name="selling_plan"]')

        if (this.checkbox) {
          this.checkbox.addEventListener('change', (e) => this.handleCheckboxChange(e))
          this.checkbox.addEventListener('keydown', (e) => this.handleCheckboxKeydown(e))
        }

        if (this.frequencyButtons.length) {
          this.frequencyButtons.forEach((button) => {
            button.addEventListener('click', (e) => this.handleFrequencyClick(e))
          })
        }

        this.updateVisibility()
      }

      handleCheckboxChange(event) {
        const isChecked = event.target.checked
        this.updateVisibility()

        if (!isChecked) {
          this.selectedSellingPlan = null
          this.updateSellingPlanInput()
          this.clearFrequencySelection()
          this.updatePrice()
        } else {
          const firstButton = this.frequencyButtons[0]
          if (firstButton) {
            const planId = parseInt(firstButton.dataset.planId)
            this.selectFrequency(firstButton, planId)
          }
        }
      }

      handleFrequencyClick(event) {
        const button = event.currentTarget
        const planId = parseInt(button.dataset.planId)
        this.selectFrequency(button, planId)
      }

      handleCheckboxKeydown(event) {
        if (event.key !== 'Enter') return

        event.preventDefault()
        this.checkbox.checked = !this.checkbox.checked
        this.checkbox.dispatchEvent(new Event('change', { bubbles: true }))
      }

      selectFrequency(button, planId) {
        this.selectedSellingPlan = planId
        this.updateSellingPlanInput()
        this.updateFrequencySelection(button)
        this.updatePrice()
      }

      updateFrequencySelection(selectedButton) {
        this.frequencyButtons.forEach((button) => {
          if (button === selectedButton) {
            button.classList.add('subscription-picker__frequency--selected')
            button.setAttribute('aria-pressed', 'true')
          } else {
            button.classList.remove('subscription-picker__frequency--selected')
            button.setAttribute('aria-pressed', 'false')
          }
        })
      }

      clearFrequencySelection() {
        this.frequencyButtons.forEach((button) => {
          button.classList.remove('subscription-picker__frequency--selected')
          button.setAttribute('aria-pressed', 'false')
        })
      }

      updateVisibility() {
        if (this.frequencyContainer) {
          const isExpanded = Boolean(this.checkbox && this.checkbox.checked)

          if (this.checkbox) {
            this.checkbox.setAttribute('aria-expanded', String(isExpanded))
          }

          if (isExpanded) {
            this.frequencyContainer.classList.remove('subscription-picker__frequencies--hidden')
            this.frequencyContainer.setAttribute('aria-hidden', 'false')
          } else {
            this.frequencyContainer.classList.add('subscription-picker__frequencies--hidden')
            this.frequencyContainer.setAttribute('aria-hidden', 'true')
          }

          this.frequencyButtons.forEach((button) => {
            button.tabIndex = isExpanded ? 0 : -1
          })
        }
      }

      updateSellingPlanInput() {
        if (this.sellingPlanInput) {
          this.sellingPlanInput.value = this.selectedSellingPlan || ''
        }
      }

      updatePrice() {
        // Delegate price rendering to Shopify's section-rendering API. The
        // raw `variant.price` / `allocation.price` exposed in product JSON
        // are always in the shop's base currency cents, so computing the
        // price client-side would ignore the active market's currency
        // conversion and show the wrong amount after a currency switch.
        //
        // Fetching the section with ?variant=X&selling_plan=Y instead lets
        // Shopify render the HTML in the correct market currency (and with
        // the selling-plan discount already applied), which we then splice
        // into the DOM.
        const productInfo = this.closest('.product__info-container')
        if (!productInfo) {
          console.warn('SubscriptionPicker: updatePrice skipped — no .product__info-container ancestor')
          return
        }

        const sectionId = this.dataset.sectionId || productInfo.dataset.section
        if (!sectionId) {
          console.warn('SubscriptionPicker: updatePrice skipped — no section id')
          return
        }

        // Prefer the explicit URL passed down from the Liquid snippet. Fall
        // back to whichever variant picker custom element the theme rendered
        // (it varies: `<variant-selects>` for dropdowns, `<variant-radios>`
        // for button swatches), and finally to the current page URL for
        // single-variant products where no picker element exists at all.
        const variantElement =
          document.querySelector(`variant-selects[data-section="${sectionId}"]`) ||
          document.querySelector(`variant-radios[data-section="${sectionId}"]`)
        const productUrl = this.dataset.productUrl || variantElement?.dataset.url || window.location.pathname
        const originalSection = variantElement?.dataset.originalSection || sectionId

        const variantId = this.selectedVariant?.id
        if (!productUrl || !variantId) {
          console.warn('SubscriptionPicker: updatePrice skipped — missing product URL or variant id', {
            productUrl,
            variantId,
          })
          return
        }

        const includeSellingPlan = Boolean(this.selectedSellingPlan && this.checkbox?.checked)
        const params = new URLSearchParams({ variant: String(variantId), section_id: originalSection })
        if (includeSellingPlan) {
          params.set('selling_plan', String(this.selectedSellingPlan))
        }

        const fetchUrl = `${productUrl}?${params.toString()}`
        const requestToken = ++this._priceRequestToken
        fetch(fetchUrl)
          .then((response) => response.text())
          .then((responseText) => {
            // Discard stale responses if the shopper changed variant or plan
            // while this request was in flight.
            if (requestToken !== this._priceRequestToken) return

            const html = new DOMParser().parseFromString(responseText, 'text/html')

            // Update the add-to-cart button's in-line price (the `.priced`
            // and optional `.compare-at-price` inside `.button__priced`).
            const srcButton = html.querySelector('.button__priced')
            const dstButton = document.querySelector(`#product-form-${sectionId} .button__priced`)
            let updatedSomething = false
            if (srcButton && dstButton) {
              const srcPriced = srcButton.querySelector('.priced')
              const dstPriced = dstButton.querySelector('.priced')
              if (srcPriced && dstPriced) {
                dstPriced.innerHTML = srcPriced.innerHTML
                updatedSomething = true
              }

              const srcCompareAt = srcButton.querySelector('.compare-at-price')
              const dstCompareAt = dstButton.querySelector('.compare-at-price')
              if (srcCompareAt && dstCompareAt) {
                dstCompareAt.innerHTML = srcCompareAt.innerHTML
              } else if (srcCompareAt && !dstCompareAt) {
                dstButton.appendChild(srcCompareAt.cloneNode(true))
              } else if (!srcCompareAt && dstCompareAt) {
                dstCompareAt.remove()
              }
            }

            // Update the main price block above the button as well, so the
            // headline price and any sale badges also reflect the new
            // subscription / variant selection.
            const srcPrice = html.getElementById(`price-${originalSection}`)
            const dstPrice = document.getElementById(`price-${sectionId}`)
            if (srcPrice && dstPrice) {
              dstPrice.innerHTML = srcPrice.innerHTML
              updatedSomething = true
            }

            if (!updatedSomething) {
              console.warn(
                'SubscriptionPicker: fetched section HTML but found nothing to splice',
                { fetchUrl, hasButtonSrc: Boolean(srcButton), hasButtonDst: Boolean(dstButton), hasPriceSrc: Boolean(srcPrice), hasPriceDst: Boolean(dstPrice) }
              )
            }
          })
          .catch((error) => {
            console.warn('SubscriptionPicker: failed to refresh price', error)
          })
      }

      getDiscountPercentage(plan) {
        if (!plan?.price_adjustments || plan.price_adjustments.length === 0) return 0
        const adjustment = plan.price_adjustments[0]
        if (adjustment.value_type === 'percentage') {
          return adjustment.value
        }
        return 0
      }

      getFrequencyText(plan) {
        if (!plan.delivery_policy?.fixed_interval) return plan.name

        const intervalCount = plan.delivery_policy.fixed_interval.interval_count
        const intervalUnit = plan.delivery_policy.fixed_interval.interval_unit

        if (intervalUnit === 'DAY') {
          return `Every ${intervalCount} days`
        } else if (intervalUnit === 'WEEK') {
          return `Every ${intervalCount} ${intervalCount === 1 ? 'week' : 'weeks'}`
        } else if (intervalUnit === 'MONTH') {
          return `Every ${intervalCount} ${intervalCount === 1 ? 'month' : 'months'}`
        }

        return plan.name
      }
    }
  )
}
